import React, { useEffect, useState, useRef } from 'react';
import { Box, Heading, HStack, VStack, Button, Input, Textarea, Table, Thead, Tbody, Tr, Th, Td, Card, CardBody, useToast, IconButton, Select, Text, Divider, Wrap, WrapItem, Tag, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';

import { FiPlus, FiTrash2, FiSave, FiEdit2, FiX } from 'react-icons/fi';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import PDFStyleEditor from '../components/PDFStyleEditor';

const SettingsPDF = () => {
  const { t } = useLanguage();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [newTpl, setNewTpl] = useState({ name: '', description: '' });
  const [lines, setLines] = useState([]);
  const [newLine, setNewLine] = useState({ line_id: '', line_x: '36', line_y: '36', font_family: 'Montserrat', font_size: 10, max_width: '', content: '', content_de: '', content_en: '' });
  const [editingId, setEditingId] = useState(null);
  const [availableFonts, setAvailableFonts] = useState([]);
  const [companyFooter, setCompanyFooter] = useState({ title_en: '', col1_en: '', col2_en: '', col3_en: '', title_de: '', col1_de: '', col2_de: '', col3_de: '' });
  const [companyFooterSaving, setCompanyFooterSaving] = useState(false);

  const handleNewLineChange = (field, value) => {
    setNewLine(prev => ({ ...prev, [field]: value }));
  };

  const handleNewTemplateChange = (field, value) => {
    setNewTpl(prev => ({ ...prev, [field]: value }));
  };

  const EditableCell = ({ rowId, field, value, isEditing, type = "text", width, placeholder }) => {
    const inputRef = useRef();
    
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.value = value || '';
      }
    }, [value, isEditing]);
    
    if (type === "textarea") {
      return (
        <Textarea
          ref={inputRef}
          defaultValue={value || ''}
          isDisabled={!isEditing}
          size="sm"
          rows={1}
          placeholder={placeholder}
          data-field={field}
          data-row-id={rowId}
        />
      );
    }
    
    return (
      <Input
        ref={inputRef}
        defaultValue={value || ''}
        type={type}
        isDisabled={!isEditing}
        size="sm"
        w={width}
        placeholder={placeholder}
        data-field={field}
        data-row-id={rowId}
      />
    );
  };

  const LineRow = ({ l }) => {
    const isEditing = editingId === l.id;
    
    return (
      <Tr>
        <Td>
          <EditableCell 
            rowId={l.id}
            field="line_id"
            value={l.line_id}
            isEditing={isEditing}
          />
        </Td>
        <Td isNumeric>
          <EditableCell 
            rowId={l.id}
            field="line_x"
            value={l.line_x}
            isEditing={isEditing}
            width="80px"
            placeholder="36 or +10"
          />
        </Td>
        <Td isNumeric>
          <EditableCell 
            rowId={l.id}
            field="line_y"
            value={l.line_y}
            isEditing={isEditing}
            width="80px"
            placeholder="36 or +12"
          />
        </Td>
        <Td>
          <EditableCell 
            rowId={l.id}
            field="font_family"
            value={l.font_family}
            isEditing={isEditing}
          />
        </Td>
        <Td isNumeric>
          <EditableCell 
            rowId={l.id}
            field="font_size"
            value={l.font_size}
            isEditing={isEditing}
            type="number"
            width="80px"
          />
        </Td>
        <Td isNumeric>
          <EditableCell 
            rowId={l.id}
            field="max_width"
            value={l.max_width}
            isEditing={isEditing}
            type="number"
            width="100px"
            placeholder="auto"
          />
        </Td>
        <Td>
          <EditableCell 
            rowId={l.id}
            field="content_de"
            value={l.content_de}
            isEditing={isEditing}
            type="textarea"
            placeholder="DE"
          />
        </Td>
        <Td>
          <EditableCell 
            rowId={l.id}
            field="content_en"
            value={l.content_en}
            isEditing={isEditing}
            type="textarea"
            placeholder="EN"
          />
        </Td>
        <Td>
          <HStack spacing={1} justify="flex-end">
            {isEditing ? (
              <>
                <IconButton 
                  aria-label={t('settings.save_label')} 
                  icon={<FiSave />} 
                  size="xs" 
                  colorScheme="green" 
                  onClick={saveEdit}
                  type="button"
                />
                <IconButton 
                  aria-label={t('settings.cancel_label')} 
                  icon={<FiX />} 
                  size="xs" 
                  variant="ghost" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    cancelEdit();
                  }}
                  type="button"
                />
              </>
            ) : (
              <>
                <IconButton 
                  aria-label={t('settings.edit_label')} 
                  icon={<FiEdit2 />} 
                  size="xs" 
                  variant="ghost" 
                  onClick={(e) => startEdit(l, e)}
                  type="button"
                />
                <IconButton 
                  aria-label={t('settings.delete_label')} 
                  icon={<FiTrash2 />} 
                  size="xs" 
                  variant="ghost" 
                  colorScheme="red" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteLine(l.id);
                  }}
                  type="button"
                />
              </>
            )}
          </HStack>
        </Td>
      </Tr>
    );
  };



  const loadTemplates = async () => {
    const res = await api.get('/api/settings/pdf/templates');
    setTemplates(res.data?.data?.templates || []);
  };
  
  const loadLines = async (id) => {
    if (!id) { setLines([]); return; }
    const res = await api.get(`/api/settings/pdf/templates/${id}/lines`);
    setLines(res.data?.data?.lines || []);
  };
  
  const loadFonts = async () => {
    const res = await api.get('/api/settings/pdf/fonts');
    setAvailableFonts(res.data?.data?.fonts || []);
  };

  useEffect(() => { 
    loadTemplates(); 
    loadFonts(); 
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => {
    loadLines(selectedId);
    // Load company footer data for selected template
    if (selectedId) {
      const tpl = templates.find(t => String(t.id) === String(selectedId));
      if (tpl) {
        setCompanyFooter({
          title_en: tpl.company_footer_title_en || '',
          col1_en: tpl.company_footer_col1_en || '',
          col2_en: tpl.company_footer_col2_en || '',
          col3_en: tpl.company_footer_col3_en || '',
          title_de: tpl.company_footer_title_de || '',
          col1_de: tpl.company_footer_col1_de || '',
          col2_de: tpl.company_footer_col2_de || '',
          col3_de: tpl.company_footer_col3_de || '',
        });
      }
    } else {
      setCompanyFooter({ title_en: '', col1_en: '', col2_en: '', col3_en: '', title_de: '', col1_de: '', col2_de: '', col3_de: '' });
    }
  }, [selectedId, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  const createTemplate = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!newTpl.name.trim()) return;
    await api.post('/api/settings/pdf/templates', newTpl);
    setNewTpl({ name: '', description: '' });
    loadTemplates();
    toast({ title: t('common.success'), status: 'success' });
  };

  const addLine = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!selectedId) { 
      toast({ title: t('common.error'), description: t('settings.select_template'), status: 'error' }); 
      return; 
    }
    
    try {
      const res = await api.post(`/api/settings/pdf/templates/${selectedId}/lines`, newLine);
      const addedLine = res.data?.data?.line;
      
      if (addedLine) {
        // Add to existing lines instead of reloading
        setLines(prev => [...prev, addedLine]);
      } else {
        // Fallback: reload if we don't get the new line back
        loadLines(selectedId);
      }
      
      setNewLine({ line_id: '', line_x: '36', line_y: '36', font_family: 'Montserrat', font_size: 10, content: '', content_de: '', content_en: '' });
      toast({ title: t('settings.saved'), status: 'success' });
    } catch (error) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  const deleteLine = async (id) => { 
    try {
      await api.delete(`/api/settings/pdf/templates/${selectedId}/lines/${id}`); 
      // Remove from state instead of reloading
      setLines(prev => prev.filter(line => line.id !== id));
      toast({ title: t('settings.deleted'), status: 'success' });
    } catch (error) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  const startEdit = (l, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingId(l.id);
  };

  const cancelEdit = () => { 
    setEditingId(null);
  };

  const saveEdit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!selectedId || !editingId) return;
    
    try {
      // Get current values from DOM inputs
      const rowInputs = document.querySelectorAll(`input[data-field], textarea[data-field]`);
      const updatedData = {};
      
      rowInputs.forEach(input => {
        const field = input.getAttribute('data-field');
        const rowId = input.getAttribute('data-row-id');
        
        if (rowId === String(editingId)) {
          let value = input.value;
          if (input.type === 'number') {
            value = Number(value) || 0;
          }
          updatedData[field] = value;
        }
      });
      
      await api.put(`/api/settings/pdf/templates/${selectedId}/lines/${editingId}`, updatedData);
      
      // Update the line in state
      setLines(prev => prev.map(line => 
        line.id === editingId 
          ? { ...line, ...updatedData }
          : line
      ));
      
      setEditingId(null);
      toast({ title: t('settings.saved'), status: 'success' });
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>{t('settings.pdf_settings')}</Heading>

      <Card mb={6}><CardBody>
        <HStack spacing={4} align="end">
          <Box>
            <Text>{t('settings.templates_label')}</Text>
            <Select value={selectedId} onChange={(e)=> setSelectedId(e.target.value)} minW="260px">
              <option value="">{t('common.select')}</option>
              {templates.map(tpl => (<option key={tpl.id} value={tpl.id}>{tpl.name}</option>))}
            </Select>
          </Box>
          <Box>
            <Text>{t('common.name')}</Text>
            <Input 
              value={newTpl.name} 
              onChange={(e) => handleNewTemplateChange('name', e.target.value)} 
              placeholder={t('settings.template_name_placeholder')} 
            />
          </Box>
          <Box>
            <Text>{t('common.description')}</Text>
            <Input 
              value={newTpl.description} 
              onChange={(e) => handleNewTemplateChange('description', e.target.value)} 
              placeholder={t('settings.description_placeholder')} 
            />
          </Box>
          <Button 
            leftIcon={<FiPlus />} 
            onClick={createTemplate}
            type="button"
          >
            {t('settings.add_button')}
          </Button>
        </HStack>
      </CardBody></Card>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>{t('settings.template_lines')}</Tab>
          <Tab>{t('settings.pdf_styles_tab', 'Styles')}</Tab>
          <Tab>{t('settings.pdf_company_footer_tab', 'Company Footer')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0} pt={4}>
      <Card><CardBody>
        <Heading size="md" mb={3}>{t('settings.template_lines')}</Heading>

        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>{t('settings.line_id')}</Th>
              <Th isNumeric>{t('settings.x_coordinate')}</Th>
              <Th isNumeric>{t('settings.y_coordinate')}</Th>
              <Th>{t('settings.font')}</Th>
              <Th isNumeric>{t('settings.size')}</Th>
              <Th isNumeric>{t('settings.max_width')}</Th>
              <Th>{t('settings.german_label')}</Th>
              <Th>{t('settings.english_label')}</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {lines.map(l => (
              <LineRow key={l.id} l={l} />
            ))}
            <Tr>
              <Td><Input 
                value={newLine.line_id} 
                onChange={(e) => handleNewLineChange('line_id', e.target.value)} 
                placeholder={t('settings.header_left_placeholder')} 
                size="sm"
              /></Td>
              <Td isNumeric><Input 
                value={newLine.line_x} 
                onChange={(e) => handleNewLineChange('line_x', e.target.value)} 
                w="80px" 
                placeholder={t('settings.coordinate_placeholder_1')} 
              /></Td>
              <Td isNumeric><Input 
                value={newLine.line_y} 
                onChange={(e) => handleNewLineChange('line_y', e.target.value)} 
                w="80px" 
                placeholder={t('settings.coordinate_placeholder_2')} 
              /></Td>
              <Td><Input 
                value={newLine.font_family} 
                onChange={(e) => handleNewLineChange('font_family', e.target.value)} 
                placeholder={t('settings.montserrat_placeholder')} 
              /></Td>
              <Td isNumeric><Input 
                type="number" 
                value={newLine.font_size} 
                onChange={(e) => handleNewLineChange('font_size', Number(e.target.value))} 
                w="80px" 
              /></Td>
              <Td isNumeric><Input 
                type="number" 
                value={newLine.max_width} 
                onChange={(e) => handleNewLineChange('max_width', e.target.value)} 
                w="100px" 
                placeholder={t('settings.auto_placeholder')} 
              /></Td>
              <Td><Textarea 
                value={newLine.content_de} 
                onChange={(e) => handleNewLineChange('content_de', e.target.value)} 
                placeholder={t('settings.german_label')} 
                rows={1} 
              /></Td>
              <Td><Textarea 
                value={newLine.content_en} 
                onChange={(e) => handleNewLineChange('content_en', e.target.value)} 
                placeholder={t('settings.english_label')} 
                rows={1} 
              /></Td>
              <Td>
                <Button 
                  leftIcon={<FiSave />} 
                  size="sm" 
                  onClick={addLine}
                  type="button"
                >
                  {t('settings.save_button')}
                </Button>
              </Td>
            </Tr>
          </Tbody>
        </Table>


        <Divider my={6} />
        <VStack align="start" spacing={2}>
          <Text fontWeight="bold">{t('settings.supported_fonts')}</Text>
          <Wrap>
            {availableFonts.length === 0 ? (
              <WrapItem><Text color="gray.500">{t('settings.no_fonts_found')}</Text></WrapItem>
            ) : availableFonts.map(f => (
              <WrapItem key={f.file}><Tag size="md" variant="subtle">{f.name}</Tag></WrapItem>
            ))}
          </Wrap>
          <Text color="gray.600" fontSize="sm">{t('settings.font_help_text')}</Text>
        </VStack>
      </CardBody></Card>
          </TabPanel>
          <TabPanel p={0} pt={4}>
            <Card><CardBody>
              <PDFStyleEditor templateId={selectedId} availableFonts={availableFonts} />
            </CardBody></Card>
          </TabPanel>
          <TabPanel p={0} pt={4}>
            <Card><CardBody>
              <Heading size="md" mb={4}>{t('settings.pdf_company_footer_title', 'Company Footer (Last Page)')}</Heading>
              {!selectedId ? (
                <Text color="gray.500">{t('settings.pdf_select_template_first', 'Select a template first.')}</Text>
              ) : (
                <VStack spacing={4} align="stretch">
                  <Tabs variant="soft-rounded" size="sm" colorScheme="gray">
                    <TabList>
                      <Tab>Deutsch</Tab>
                      <Tab>English</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel px={0}>
                        <VStack spacing={3} align="stretch">
                          <Box>
                            <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.title', 'Title')}</Text>
                            <Input value={companyFooter.title_de} onChange={(e) => setCompanyFooter(prev => ({ ...prev, title_de: e.target.value }))} placeholder="Severich & Partner GmbH & Co. KG" />
                          </Box>
                          <HStack spacing={4} align="start">
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.col1_label', 'Column 1 (Address)')}</Text>
                              <Textarea value={companyFooter.col1_de} onChange={(e) => setCompanyFooter(prev => ({ ...prev, col1_de: e.target.value }))} rows={6} placeholder={"Vennstraße 21\n52159 Roetgen\nTel.: ..."} />
                            </Box>
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.col2_label', 'Column 2 (Legal)')}</Text>
                              <Textarea value={companyFooter.col2_de} onChange={(e) => setCompanyFooter(prev => ({ ...prev, col2_de: e.target.value }))} rows={6} placeholder={"Registergericht:\nAmtsgericht..."} />
                            </Box>
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.col3_label', 'Column 3 (Bank)')}</Text>
                              <Textarea value={companyFooter.col3_de} onChange={(e) => setCompanyFooter(prev => ({ ...prev, col3_de: e.target.value }))} rows={6} placeholder={"Bankverbindung:\nIBAN: ..."} />
                            </Box>
                          </HStack>
                        </VStack>
                      </TabPanel>
                      <TabPanel px={0}>
                        <VStack spacing={3} align="stretch">
                          <Box>
                            <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.title', 'Title')}</Text>
                            <Input value={companyFooter.title_en} onChange={(e) => setCompanyFooter(prev => ({ ...prev, title_en: e.target.value }))} placeholder="Severich & Partner GmbH & Co. KG" />
                          </Box>
                          <HStack spacing={4} align="start">
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.col1_label', 'Column 1 (Address)')}</Text>
                              <Textarea value={companyFooter.col1_en} onChange={(e) => setCompanyFooter(prev => ({ ...prev, col1_en: e.target.value }))} rows={6} placeholder={"Vennstraße 21\n52159 Roetgen\nTel.: ..."} />
                            </Box>
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.col2_label', 'Column 2 (Legal)')}</Text>
                              <Textarea value={companyFooter.col2_en} onChange={(e) => setCompanyFooter(prev => ({ ...prev, col2_en: e.target.value }))} rows={6} placeholder={"Court of Registration:\nDistrict Court..."} />
                            </Box>
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>{t('settings.pdf_footer.col3_label', 'Column 3 (Bank)')}</Text>
                              <Textarea value={companyFooter.col3_en} onChange={(e) => setCompanyFooter(prev => ({ ...prev, col3_en: e.target.value }))} rows={6} placeholder={"Bank Details:\nIBAN: ..."} />
                            </Box>
                          </HStack>
                        </VStack>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                  <Button
                    colorScheme="blue"
                    isLoading={companyFooterSaving}
                    onClick={async () => {
                      setCompanyFooterSaving(true);
                      try {
                        await api.put(`/api/settings/pdf/templates/${selectedId}`, {
                          company_footer_title_en: companyFooter.title_en,
                          company_footer_col1_en: companyFooter.col1_en,
                          company_footer_col2_en: companyFooter.col2_en,
                          company_footer_col3_en: companyFooter.col3_en,
                          company_footer_title_de: companyFooter.title_de,
                          company_footer_col1_de: companyFooter.col1_de,
                          company_footer_col2_de: companyFooter.col2_de,
                          company_footer_col3_de: companyFooter.col3_de,
                        });
                        loadTemplates();
                        toast({ title: t('settings.saved'), status: 'success' });
                      } catch (e) {
                        toast({ title: t('common.error'), status: 'error' });
                      }
                      setCompanyFooterSaving(false);
                    }}
                    alignSelf="flex-end"
                  >
                    {t('settings.save_button') || 'Save'}
                  </Button>
                </VStack>
              )}
            </CardBody></Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default SettingsPDF;


